import { Link } from '@tanstack/react-router';
import { PackageX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';

import type { DeliveryCompareBlockedReason } from '../../types/delivery-compare.types';

/**
 * Full-page empty state for a batch that reached this route (a stale deep
 * link, or a link shared before the batch delivered) but isn't eligible —
 * see `getDeliveryCompareEligibility`. Every reason routes back to the batch
 * detail page rather than dead-ending.
 */
export function DeliveryCompareBlocked({
  reason,
  batchId,
}: {
  reason: DeliveryCompareBlockedReason;
  batchId: string;
}) {
  const { t } = useTranslation('catalog-operations');
  return (
    <div
      className="bg-background flex min-h-[50vh] flex-col items-center justify-center gap-3 rounded-xl border p-10 text-center"
      data-testid="delivery-compare-blocked"
    >
      <PackageX className="text-muted-foreground h-10 w-10" aria-hidden="true" />
      <p
        className="text-foreground text-base font-semibold"
        data-testid="delivery-compare-blocked-title"
      >
        {t(`deliveryCompare.blocked.${reason}.title`)}
      </p>
      <p className="text-muted-foreground max-w-md text-sm">
        {t(`deliveryCompare.blocked.${reason}.body`)}
      </p>
      <Button variant="outline" className="cursor-pointer" asChild>
        <Link
          to="/catalog-operations/$batchId"
          params={{ batchId }}
          data-testid="delivery-compare-blocked-back-link"
        >
          {t('deliveryCompare.blocked.backToBatch')}
        </Link>
      </Button>
    </div>
  );
}
